/** API client for frontend communication with the backend. */
import { beginProcessing, endProcessing, processingLabelForMethod } from './processing';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export type ApiRequestOptions = RequestInit & {
  /** Show the global processing overlay (default: true for non-GET). */
  trackProcessing?: boolean;
  processingLabel?: string;
};

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  async request<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
    const { trackProcessing, processingLabel, ...fetchOptions } = options;
    const method = (fetchOptions.method || 'GET').toUpperCase();
    const track =
      trackProcessing !== undefined ? trackProcessing : method !== 'GET';
    if (track) {
      beginProcessing(processingLabel || processingLabelForMethod(method));
    }
    try {
      const url = `${this.baseUrl}${path}`;
      const response = await fetch(url, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...fetchOptions.headers,
        },
        ...fetchOptions,
      });

      if (!response.ok) {
        const error = await response.text();
        let detail = error;
        try {
          const parsed = JSON.parse(error) as { detail?: unknown; message?: unknown };
          if (typeof parsed.detail === 'string' && parsed.detail.trim()) {
            detail = parsed.detail.trim();
          } else if (typeof parsed.message === 'string' && parsed.message.trim()) {
            detail = parsed.message.trim();
          }
        } catch {
          /* keep raw body */
        }
        throw new Error(detail || `HTTP ${response.status}`);
      }

      if (response.status === 204) {
        return undefined as T;
      }
      const text = await response.text();
      if (!text) return undefined as T;
      return JSON.parse(text) as T;
    } finally {
      if (track) endProcessing();
    }
  }

  async get<T>(path: string, options?: ApiRequestOptions): Promise<T> {
    return this.request<T>(path, { ...options, method: 'GET' });
  }

  async post<T>(path: string, data?: unknown, options?: ApiRequestOptions): Promise<T> {
    return this.request<T>(path, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(path: string, data?: unknown, options?: ApiRequestOptions): Promise<T> {
    return this.request<T>(path, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(path: string, data?: unknown, options?: ApiRequestOptions): Promise<T> {
    return this.request<T>(path, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(path: string, options?: ApiRequestOptions): Promise<T> {
    return this.request<T>(path, { ...options, method: 'DELETE' });
  }

  async upload<T>(
    path: string,
    file: File,
    options?: { processingLabel?: string; trackProcessing?: boolean },
  ): Promise<T> {
    const track = options?.trackProcessing !== false;
    if (track) beginProcessing(options?.processingLabel || 'Importing…');
    try {
      const body = new FormData();
      body.append('file', file);
      const response = await fetch(`${this.baseUrl}${path}`, {
        method: 'POST',
        credentials: 'include',
        body,
      });
      if (!response.ok) {
        const error = await response.text();
        throw new Error(`HTTP ${response.status}: ${error}`);
      }
      return response.json();
    } finally {
      if (track) endProcessing();
    }
  }

  async download(path: string, filename: string): Promise<void> {
    beginProcessing('Downloading…');
    try {
      const response = await fetch(`${this.baseUrl}${path}`, { credentials: 'include' });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      endProcessing();
    }
  }
}

export const api = new ApiClient(API_BASE_URL);
